/**
 * hostRouting — ONE answer to "what does this hostname mean?"
 *
 * Every product on agri-books.com is reached by a subdomain, and a name can
 * belong to exactly one owner (owner rule 2026-09-16: "whoever grabs it first").
 * This module is the frontend half of that rule: the app entry asks
 * `resolveHost()` and renders whatever kind comes back. Nothing else in the
 * codebase should sniff `window.location.hostname` for routing.
 *
 * Kinds today:
 *   main           agri-books.com / www / localhost / an IP — the platform front door
 *   dev            dev*, test*, staging*, preview* — per-machine tunnels, behave like main
 *   reserved       infrastructure names (api, mail, static, …) — behave like main
 *   product        a first-party product owning the whole host (OrbitPrint)
 *   sports-tenant  any other name → a Sports facility's public booking site
 *
 * Later (not built): `pos-org` — e.g. sibugay.agri-books.com as the login page for
 * that POS organization's members. It becomes one more entry once the backend
 * claims registry (which makes first-come-first-served real across Sports, POS and
 * products) exists; see docs/ORBITPRINT-CONCEPT.md → host routing.
 *
 * `?__sub=name` on any host forces a tenant sub for local testing (unchanged).
 */

export const PLATFORM_DOMAIN = 'agri-books.com';

// First-party products that own their host outright. Add a line here when a
// product gets a subdomain; also add its name to the backend reserved list so a
// tenant can never claim it.
export const PRODUCT_HOSTS = {
  orbitprint: 'orbit',      // OrbitPrint HUB / web app — owner rename 2026-09-16 (was "print")
};

// Infrastructure names that are never a tenant site.
export const RESERVED_SUBS = ['www', 'api', 'admin', 'app', 'mail', 'static', 'localhost'];

const SUB_RE = /^([a-z0-9][a-z0-9-]*[a-z0-9]?)\.agri-books\.com$/i;
const DEV_RE = /^(dev|test|staging|preview)[a-z0-9-]*$/i;

export function subdomainOf(hostname) {
  const m = (hostname || '').match(SUB_RE);
  return m ? m[1].toLowerCase() : null;
}

/**
 * @param {string} hostname   e.g. window.location.hostname
 * @param {string|null} forcedSub  the `__sub` query param, if any
 * @returns {{kind: string, sub: string|null, product?: string}}
 */
export function resolveHost(hostname, forcedSub = null) {
  const sub = subdomainOf(hostname);
  if (sub && PRODUCT_HOSTS[sub]) return { kind: 'product', sub, product: PRODUCT_HOSTS[sub] };
  if (sub && RESERVED_SUBS.includes(sub)) return forcedSub ? { kind: 'sports-tenant', sub: forcedSub } : { kind: 'reserved', sub };
  if (sub && DEV_RE.test(sub)) return forcedSub ? { kind: 'sports-tenant', sub: forcedSub } : { kind: 'dev', sub };
  if (sub) return { kind: 'sports-tenant', sub };
  if (forcedSub) return { kind: 'sports-tenant', sub: forcedSub };
  return { kind: 'main', sub: null };
}

export function resolveCurrentHost() {
  if (typeof window === 'undefined') return { kind: 'main', sub: null };
  const forced = new URLSearchParams(window.location.search).get('__sub');
  return resolveHost(window.location.hostname, forced);
}
